<script lang="ts">
    import { onMount } from 'svelte';
    import { link } from 'svelte-spa-router';
    import RememberMeBanner from '../../lib/Actions/RememberMeBanner.svelte';
    import ActionForm from '../../lib/Actions/ActionForm.svelte';
    import EmailVerification from '../../lib/EmailVerification.svelte';
    import Modal from '../../lib/Modal.svelte';
    import type { Event, EventRegistration } from '../../api/events';
    import {
        getEventById,
        getEventRegistration,
        registerForEvent,
        cancelEventRegistration,
        resolveEventId,
        resolveEventUserToken,
        isEventRegistrationFull,
        formatEventScheduleDisplay
    } from '../../api/events';
    import { ApiResponseError, associationIdName, getQueryParam, vvSettings } from '../../api/common';
    import {
        getPetitionFormSettings,
        type FormFieldSettings,
        type CustomField
    } from '../../api/formSettings';
    import { checkAddress, type Address } from '../../api/address';
    import {
        getUser,
        postUser,
        putUser,
        postMemberships,
        postRememberMeCookies,
        deleteRememberMeCookies,
        type User,
        type UserIdentity
    } from '../../api/users';
    import * as t from '../../translations/Events.ts';
    import * as tCommon from '../../translations/Common.ts';
    import RegisterPageSkeleton from '../../lib/Events/RegisterPageSkeleton.svelte';
    import EventAccordion from '../../lib/Events/EventAccordion.svelte';
    import CkEditorContent from '../../lib/CkEditorContent.svelte';
    import Button from '../../lib/Button.svelte';
    import { setEventConfirmationData } from '../../stores/eventsConfirmationStore';

    export let params: { id?: string } = {};

    function getSiteSegment(): string {
        const rawSiteNumber = (vvSettings as any)?.siteNumber;
        return rawSiteNumber ? `/${rawSiteNumber}` : '';
    }

    function getEventsListUrl(): string {
        return `/${associationIdName}${getSiteSegment()}/Events`;
    }

    /** Full-page navigation so ConfirmRegistration MVC action injects tracking tags. */
    function navigateToConfirmation(eventId: number, userToken: string): void {
        const params = new URLSearchParams();
        params.set('user', userToken);
        const vvcgRD = getQueryParam('vvcgRD');
        const vvsn = getQueryParam('vvsn');
        if (vvcgRD) params.set('vvcgRD', vvcgRD);
        if (vvsn) params.set('vvsn', vvsn);
        if (getQueryParam('fromSingle') === '1') params.set('fromSingle', '1');
        const q = params.toString() ? `?${params.toString()}` : '';
        window.location.href = `/${associationIdName}${getSiteSegment()}/Events/${eventId}/ConfirmRegistration${q}`;
    }
    
    let loading = true;
    let submitting = false;
    let error: string | null = null;
    let event: Event | null = null;
    let registration: EventRegistration | null = null;
    
    // Form state
    let firstName = '';
    let lastName = '';
    let email = '';
    let honorific = '';
    let suffix = '';
    let phone = '';
    let fax = '';
    let mobileNumber = '';
    let street = '';
    let city = '';
    let state = '';
    let zip = '';
    let businessName = '';
    let businessTitle = '';
    let businessStreet = '';
    let businessCity = '';
    let businessState = '';
    let businessZipCode = '';
    let preferredContactMethod: 'email' | 'phone' | 'either' = 'email';
    let specialNeeds = '';
    let notes = '';
    let firstTimeAttendee = false;
    let rememberMe = true;
    let subscribe = true;
    let textSubscribe = false;
    let customFieldValues: Record<number, string> = {};
    let validatedHomeAddress: Address | null = null;
    let validatedBusinessAddress: Address | null = null;
    /** Snapshot from loaded registration — used to detect edits (Petitions SignPage). */
    let baselineHomeAddress: { streetAddress: string; city: string; state: string; zipCode: string } | null = null;
    let baselineBusinessAddress: { streetAddress: string; city: string; state: string; zipCode: string } | null = null;
    const eventFormConfig = getPetitionFormSettings();

    // vvSettings.petitionFormSettings (read by getPetitionFormSettings) is a Petitions-only
    // global that Events never populates, so mobileNumberVisible/showTextSubscribeCheckbox and
    // the SMS-disclaimer/link fields stay at their defaults unless overlaid here from the
    // association data Events *does* receive: window.vvSettings.userContainer (built by the
    // same UserContainerModelFactory Petitions uses).
    const eventUserContainer = (vvSettings as {
        userContainer?: {
            associationDisplayName?: string;
            privacyPolicyLink?: string;
            termsAndConditionsLink?: string;
            privacyPolicyLinkText?: string;
            termsAndConditionsLinkText?: string;
            mobileSubscriptionMessage?: string;
            associationPhoneNumber?: string;
            displaySettings?: {
                mobileNumberVisible?: boolean;
                textingSubscriptionText?: string;
            };
        };
    })?.userContainer;

    // Events reuse the petition form config for now (same custom fields, address validation, etc.),
    // but we must drop the petition-specific submit text so ActionForm falls back to our event
    // `submitLabel` ("Complete Registration" / "Update Registration") instead of "Sign this petition".
    let formSettings: FormFieldSettings = {
        ...eventFormConfig.fieldSettings,
        submitButtonText: undefined,
        mobileNumberVisible: eventUserContainer?.displaySettings?.mobileNumberVisible ?? eventFormConfig.fieldSettings.mobileNumberVisible,
        showTextSubscribeCheckbox: eventUserContainer?.displaySettings?.mobileNumberVisible ?? eventFormConfig.fieldSettings.showTextSubscribeCheckbox,
        textingSubscriptionText: eventUserContainer?.displaySettings?.textingSubscriptionText ?? eventFormConfig.fieldSettings.textingSubscriptionText,
        associationDisplayName: eventUserContainer?.associationDisplayName ?? eventFormConfig.fieldSettings.associationDisplayName,
        privacyPolicyLink: eventUserContainer?.privacyPolicyLink ?? eventFormConfig.fieldSettings.privacyPolicyLink,
        termsAndConditionsLink: eventUserContainer?.termsAndConditionsLink ?? eventFormConfig.fieldSettings.termsAndConditionsLink,
        privacyPolicyLinkText: eventUserContainer?.privacyPolicyLinkText ?? eventFormConfig.fieldSettings.privacyPolicyLinkText,
        termsAndConditionsLinkText: eventUserContainer?.termsAndConditionsLinkText ?? eventFormConfig.fieldSettings.termsAndConditionsLinkText,
        mobileSubscriptionMessage: eventUserContainer?.mobileSubscriptionMessage ?? eventFormConfig.fieldSettings.mobileSubscriptionMessage
    };
    let customFields: CustomField[] = eventFormConfig.customFields;
    
    // UI state
    let showUpdateForm = false;
    let showUnregisterConfirm = false;
    let showRememberBanner = false;
    let isReturningUserEditing = false;
    let rememberedUser: {
        firstName: string;
        lastName: string;
        email: string;
        address?: {
            street?: string;
            city?: string;
            state?: string;
            zipCode?: string;
        };
    } | null = null;
    let rememberedToken: string | undefined;
    let rememberedUserId: number | undefined;

    // Email ownership verification (mirrors Petitions SignPage)
    let isAwaitingVerification = false;
    let verificationEmailAddress = '';
    let verifiedIdentity: UserIdentity | null = null;
    let identityChoices: UserIdentity[] = [];
    let selectedIdentityToken = '';
    let isChoosingIdentity = false;
    let isUpdatingExistingUser = false;
    /** True while one-click register runs after the remember-me banner (Petitions SignPage). */
    let useRememberedInfo = false;
    /** Guards duplicate loads; enables SPA navigation between events (Petitions SignPage). */
    let currentLoadedEventId: string | undefined;

    onMount(() => {
        if (params.id) {
            currentLoadedEventId = params.id;
            void loadEvent();
        }
    });

    // Re-load when hash route changes (/Register/8005 → /Register/8006) without remounting.
    $: if (params.id && params.id !== currentLoadedEventId) {
        currentLoadedEventId = params.id;
        void loadEvent();
    }

    function clearFormFields() {
        firstName = '';
        lastName = '';
        email = '';
        honorific = '';
        suffix = '';
        phone = '';
        fax = '';
        mobileNumber = '';
        street = '';
        city = '';
        state = '';
        zip = '';
        businessName = '';
        businessTitle = '';
        businessStreet = '';
        businessCity = '';
        businessState = '';
        businessZipCode = '';
        preferredContactMethod = 'email';
        specialNeeds = '';
        notes = '';
        firstTimeAttendee = false;
        customFieldValues = {};
        validatedHomeAddress = null;
        validatedBusinessAddress = null;
        baselineHomeAddress = null;
        baselineBusinessAddress = null;
        showUpdateForm = false;
        showUnregisterConfirm = false;
        error = null;
    }

    function applyRegistrationToForm(reg: EventRegistration) {
        firstName = reg.firstName || '';
        lastName = reg.lastName || '';
        email = reg.email || '';
        phone = reg.phone || '';
        mobileNumber = reg.mobileNumber || '';
        fax = reg.fax || '';
        street = reg.address || '';
        city = reg.city || '';
        state = reg.state || '';
        zip = reg.zip || '';
        businessStreet = reg.businessStreet || '';
        businessCity = reg.businessCity || '';
        businessState = reg.businessState || '';
        businessZipCode = reg.businessZip || '';
        businessName = reg.businessName || '';
        businessTitle = reg.businessTitle || '';
        preferredContactMethod = reg.preferredContactMethod || 'email';
        specialNeeds = reg.specialNeeds || '';
        notes = reg.notes || '';
        firstTimeAttendee = reg.firstTimeAttendee || false;

        baselineHomeAddress = street.trim() && zip.trim()
            ? { streetAddress: street.trim(), city: city.trim(), state: state.trim(), zipCode: zip.trim() }
            : null;
        baselineBusinessAddress = businessStreet.trim() && businessZipCode.trim()
            ? {
                streetAddress: businessStreet.trim(),
                city: businessCity.trim(),
                state: businessState.trim(),
                zipCode: businessZipCode.trim()
            }
            : null;
    }

    function addressesMatch(
        current: { streetAddress: string; city: string; state: string; zipCode: string },
        baseline: typeof baselineHomeAddress
    ): boolean {
        if (!baseline && !current.streetAddress && !current.zipCode) return true;
        if (!baseline) return false;
        const norm = (s: string) => s.trim().toLowerCase();
        return norm(current.streetAddress) === norm(baseline.streetAddress)
            && norm(current.city) === norm(baseline.city)
            && norm(current.state) === norm(baseline.state)
            && norm(current.zipCode) === norm(baseline.zipCode);
    }

    /**
     * ActionForm requires a USPS-validated checksum before submit. Pre-filled fields from
     * GET Registration do not include a checksum — resolve one via checkAddress (Petitions).
     */
    async function hydrateValidatedAddressesFromFields() {
        if (formSettings.homeSectionVisible && street.trim() && zip.trim()) {
            const unchanged = addressesMatch(
                { streetAddress: street, city, state, zipCode: zip },
                baselineHomeAddress
            );
            if (!validatedHomeAddress?.checksum || !unchanged) {
                try {
                    const response = await checkAddress(street, zip, city, state);
                    const match = response.addresses?.find((a) => a.checksum)
                        ?? (response.addresses?.length === 1 ? response.addresses[0] : undefined);
                    if (match?.checksum) {
                        validatedHomeAddress = match;
                        if (match.city) city = match.city;
                        if (match.state) state = match.state;
                    }
                } catch (e) {
                    console.warn('Could not hydrate home address validation:', e);
                }
            }
        }

        if (formSettings.businessSectionVisible && formSettings.businessAddressVisible
            && businessStreet.trim() && businessZipCode.trim()) {
            const unchanged = addressesMatch(
                {
                    streetAddress: businessStreet,
                    city: businessCity,
                    state: businessState,
                    zipCode: businessZipCode
                },
                baselineBusinessAddress
            );
            if (!validatedBusinessAddress?.checksum || !unchanged) {
                try {
                    const response = await checkAddress(businessStreet, businessZipCode, businessCity, businessState);
                    const match = response.addresses?.find((a) => a.checksum)
                        ?? (response.addresses?.length === 1 ? response.addresses[0] : undefined);
                    if (match?.checksum) {
                        validatedBusinessAddress = match;
                        if (match.city) businessCity = match.city;
                        if (match.state) businessState = match.state;
                    }
                } catch (e) {
                    console.warn('Could not hydrate business address validation:', e);
                }
            }
        }
    }

    async function openUpdateRegistrationForm() {
        showUpdateForm = true;
        await hydrateValidatedAddressesFromFields();
    }

    async function loadEvent() {
        if (!params.id) {
            error = 'Event ID is required';
            loading = false;
            return;
        }

        const routeEventId = parseInt(params.id, 10);
        if (!routeEventId) {
            error = 'Event ID is required';
            loading = false;
            return;
        }

        try {
            loading = true;
            error = null;
            clearFormFields();
            registration = null;
            event = null;

            const userToken = await resolveEventUserToken();

            event = await getEventById(routeEventId, userToken);
            registration = await getEventRegistration(routeEventId, userToken, event);
            await loadRememberedUser();

            if (registration.isRegistered) {
                applyRegistrationToForm(registration);
                showRememberBanner = false;
            } else if (rememberedUser && showRememberBanner) {
                // Prefill bindings for Edit / submit path; form stays hidden until Edit (Petitions).
                populateFormFromRememberedUser();
            }
        } catch (e) {
            error = t.Loading_Error();
            console.error('Failed to load event:', e);
        } finally {
            loading = false;
        }
    }

    async function loadRememberedUser() {
        try {
            const remembered = await getUser();
            if (!remembered?.userToken) {
                rememberedUser = null;
                rememberedToken = undefined;
                rememberedUserId = undefined;
                showRememberBanner = false;
                return;
            }

            rememberedToken = remembered.userToken;
            rememberedUserId = remembered.userId;
            rememberedUser = {
                firstName: remembered.givenNames || '',
                lastName: remembered.surname || '',
                email: remembered.emailAddress || '',
                address: remembered.homeAddress ? {
                    street: remembered.homeAddress.streetAddress,
                    city: remembered.homeAddress.city,
                    state: remembered.homeAddress.state,
                    zipCode: remembered.homeAddress.zipCode
                } : undefined
            };

            if (!registration?.isRegistered) {
                showRememberBanner = true;
            }
        } catch {
            rememberedUser = null;
            rememberedToken = undefined;
            rememberedUserId = undefined;
            showRememberBanner = false;
        }
    }

    function populateFormFromRememberedUser() {
        if (!rememberedUser) return;
        firstName = rememberedUser.firstName || '';
        lastName = rememberedUser.lastName || '';
        email = rememberedUser.email || '';
        street = rememberedUser.address?.street || '';
        city = rememberedUser.address?.city || '';
        state = rememberedUser.address?.state || '';
        zip = rememberedUser.address?.zipCode || '';
    }

    function buildCustomFieldPayload() {
        return customFields.flatMap((field) => {
            const rawValue = customFieldValues[field.id];
            if (rawValue === undefined || rawValue === null) return [];
            if (typeof rawValue === 'string' && rawValue.trim() === '') return [];
            return [{ id: field.id, values: [rawValue] }];
        });
    }

    function extractIdentityChoices(data: unknown): UserIdentity[] {
        const parsedData = typeof data === 'string'
            ? (() => {
                try {
                    return JSON.parse(data);
                } catch {
                    return [];
                }
            })()
            : data;

        if (!Array.isArray(parsedData)) return [];

        return parsedData.filter((identity): identity is UserIdentity =>
            identity
            && typeof identity.id === 'number'
            && typeof identity.givenNames === 'string'
            && typeof identity.surname === 'string'
            && typeof identity.token === 'string'
        );
    }

    function openIdentityChooser(choices: UserIdentity[]) {
        identityChoices = choices;
        selectedIdentityToken = choices[0]?.token || '';
        isChoosingIdentity = choices.length > 1;

        if (choices.length === 1) {
            verifiedIdentity = choices[0];
            void handleSubmit();
        }
    }

    function handleVerifiedUser(identity: UserIdentity) {
        verifiedIdentity = identity;
        isAwaitingVerification = false;
        void handleSubmit();
    }

    function handleVerificationFailure() {
        error = 'We could not verify your email address. Please try again.';
    }

    function handleVerificationNotFound() {
        error = 'We could not find a matching user for that verification code.';
    }

    function handleIdentityChoiceConfirm() {
        const selectedIdentity = identityChoices.find((identity) => identity.token === selectedIdentityToken);
        if (!selectedIdentity) return;

        verifiedIdentity = selectedIdentity;
        isChoosingIdentity = false;
        void handleSubmit();
    }

    function handleIdentityChoiceCancel() {
        isChoosingIdentity = false;
        identityChoices = [];
        selectedIdentityToken = '';
        verifiedIdentity = null;
    }

    async function saveUserProfile(): Promise<User> {
        const userPayload: User = {
            honorific: honorific || undefined,
            givenNames: firstName,
            surname: lastName,
            emailAddress: email,
            phoneNumber: phone || undefined,
            mobileNumber: mobileNumber || undefined,
            rememberMe
        };

        if (validatedHomeAddress?.checksum) {
            userPayload.homeAddress = {
                streetAddress: validatedHomeAddress.streetAddress,
                city: validatedHomeAddress.city,
                state: validatedHomeAddress.state,
                zipCode: validatedHomeAddress.zipCode,
                zipCodeExtension: validatedHomeAddress.zipCodeExtension,
                county: validatedHomeAddress.county,
                country: validatedHomeAddress.country || formSettings.country || 'US',
                checksum: validatedHomeAddress.checksum
            };
        }

        if (validatedBusinessAddress?.checksum) {
            userPayload.businessAddress = {
                streetAddress: validatedBusinessAddress.streetAddress,
                city: validatedBusinessAddress.city,
                state: validatedBusinessAddress.state,
                zipCode: validatedBusinessAddress.zipCode,
                zipCodeExtension: validatedBusinessAddress.zipCodeExtension,
                county: validatedBusinessAddress.county,
                country: validatedBusinessAddress.country || formSettings.country || 'US',
                checksum: validatedBusinessAddress.checksum
            };
        }

        if (businessName) {
            (userPayload as any).businessCompanyName = businessName;
        }
        if (businessTitle) {
            (userPayload as any).businessPositionName = businessTitle;
        }
        if (suffix) {
            (userPayload as any).suffix = suffix;
        }
        if (fax) {
            (userPayload as any).faxNumber = fax;
        }

        const customPayload = buildCustomFieldPayload();
        if (customPayload.length > 0) {
            userPayload.customFieldValues = customPayload;
        }

        // Only attach the remembered token if the form is still pointing at the
        // same email. The backend's PUT /Api/Users/{token} endpoint refuses
        // (409 Conflict, "That email address is already in use") to rewrite a
        // remembered user's email to one that belongs to a different existing
        // account -- correct security behaviour. When the form email diverges
        // from the remembered email we drop the token entirely, so the call
        // falls through to POST /Api/Users which performs the standard
        // create-or-find-by-email lookup. This is the same effective behaviour
        // the rest of the Action Center exposes for returning users: come back
        // with your real email, get matched to your existing account, register.
        const formEmailNormalised = email.trim().toLowerCase();
        const rememberedEmailNormalised = rememberedUser?.email.trim().toLowerCase() ?? '';
        const formMatchesRememberedEmail = !!rememberedEmailNormalised
            && formEmailNormalised === rememberedEmailNormalised;

        if (rememberedToken && formMatchesRememberedEmail) {
            userPayload.userToken = rememberedToken;
            userPayload.userId = rememberedUserId;
        }

        if (!userPayload.userToken && verifiedIdentity) {
            userPayload.userId = verifiedIdentity.id;
            userPayload.userToken = verifiedIdentity.token;
        }

        isUpdatingExistingUser = Boolean(userPayload.userToken);

        let savedUser: User | null;
        try {
            savedUser = userPayload.userToken
                ? await putUser(userPayload)
                : await postUser(userPayload);
        } catch (saveError) {
            // Belt-and-suspenders: if a PUT still slips through with a stale
            // remembered token (e.g. a localhost remember-me cookie left over
            // from previous testing) and the server says the email belongs to
            // someone else, drop the token and retry as a POST so the create-
            // or-find-by-email path takes over. Without this, the user is
            // permanently stuck with a 409 until they manually clear cookies.
            const isEmailConflict = saveError instanceof ApiResponseError
                && saveError.statusCode === 409;
            if (!isEmailConflict || !userPayload.userToken) {
                throw saveError;
            }
            delete userPayload.userToken;
            delete userPayload.userId;
            savedUser = await postUser(userPayload);
        }

        if (!savedUser?.userToken) {
            throw new Error('Failed to save user information');
        }

        savedUser.membershipReceivesMessages = subscribe;
        savedUser.membershipReceivesTextMessages = textSubscribe;
        savedUser.mobileNumber = mobileNumber || undefined;
        await postMemberships(savedUser);

        if (rememberMe) {
            await postRememberMeCookies(savedUser);
        } else {
            await deleteRememberMeCookies();
        }

        rememberedToken = savedUser.userToken;
        rememberedUserId = savedUser.userId;
        return savedUser;
    }

    async function continueAsRememberedUser() {
        if (!rememberedUser) return;
        isReturningUserEditing = false;
        useRememberedInfo = true;
        showRememberBanner = false;
        populateFormFromRememberedUser();
        await handleSubmit();
    }

    function editRememberedUser() {
        if (!rememberedUser) return;
        isReturningUserEditing = true;
        showRememberBanner = false;
        verifiedIdentity = null;
        populateFormFromRememberedUser();
    }

    async function clearRememberedUser() {
        showRememberBanner = false;
        useRememberedInfo = false;
        rememberedUser = null;
        rememberedToken = undefined;
        rememberedUserId = undefined;
        isReturningUserEditing = false;
        isUpdatingExistingUser = false;
        verifiedIdentity = null;
        isAwaitingVerification = false;
        isChoosingIdentity = false;
        identityChoices = [];
        selectedIdentityToken = '';
        verificationEmailAddress = '';

        try {
            await deleteRememberMeCookies();
        } catch {
            // Best-effort cleanup.
        }

        // Drop injected / example tokens so the next submit creates a fresh user (Petitions handleNotMe).
        const userContainer = (vvSettings as { userContainer?: { userModel?: { userToken?: string; userId?: number } } })
            ?.userContainer;
        if (userContainer?.userModel) {
            userContainer.userModel.userToken = '';
            userContainer.userModel.userId = 0;
        }

        rememberMe = (vvSettings as { rememberMeDefault?: boolean })?.rememberMeDefault ?? true;
        subscribe = formSettings.subscribeCheckedDefault ?? false;
        textSubscribe = formSettings.textSubscribeCheckedDefault ?? false;

        clearFormFields();
    }
    
    function getRouteEventId(): number {
        return parseInt(params.id ?? '', 10) || 0;
    }

    async function handleSubmit() {
        if (!event) return;

        const eventId = resolveEventId(event, getRouteEventId());
        if (!eventId) {
            error = t.Registration_Error();
            return;
        }

        if (isRegistrationFull && !isRegistered) {
            error = t.Registration_Full_Message();
            return;
        }
        
        try {
            submitting = true;
            error = null;

            // Persist the form data as a User first. postUser/putUser either
            // creates the user (anonymous flow) or updates an existing one
            // (remembered/identified) and returns a fresh server-issued
            // userToken + userId. That pair -- not the mock token sitting in
            // vvSettings -- is what registerForEvent must carry so the REST
            // controller can resolve the registering user. Same approach
            // Petitions uses in SignPage.svelte (step 6: signPetition).
            let savedUser: User;
            try {
                savedUser = await saveUserProfile();
            } catch (profileError) {
                if (profileError instanceof ApiResponseError) {
                    if (profileError.statusCode === 300) {
                        const choices = extractIdentityChoices(profileError.data);
                        if (choices.length > 0) {
                            openIdentityChooser(choices);
                            return;
                        }
                    }

                    // Email already tied to an existing account -- send verification
                    // code and resume with the verified identity (Petitions SignPage).
                    if (profileError.statusCode === 409 && !isUpdatingExistingUser) {
                        verificationEmailAddress = email;
                        isAwaitingVerification = true;
                        return;
                    }

                    error = profileError.message || t.Registration_Error();
                    return;
                }
                throw profileError;
            }

            const registrationToken = savedUser.userToken?.trim() || rememberedToken?.trim();
            if (!registrationToken) {
                error = t.Registration_Error();
                console.error('Registration failed: missing user token after profile save', savedUser);
                return;
            }

            const result = await registerForEvent({
                eventId,
                firstName,
                lastName,
                email,
                phone,
                mobileNumber,
                fax,
                address: street,
                city,
                state,
                zip,
                businessStreet,
                businessCity,
                businessState,
                businessZip: businessZipCode,
                businessName,
                businessTitle,
                country: formSettings.country || 'US',
                preferredContactMethod,
                specialNeeds,
                notes,
                firstTimeAttendee,
                includeFirstTimeAttendee: Boolean(event?.showOtherRegistrationInformation),
                rememberMe,
                userToken: registrationToken,
            }, registrationToken);
            
            if (result.success) {
                setEventConfirmationData({
                    eventId,
                    thankYouMessage: result.message
                });
                navigateToConfirmation(eventId, registrationToken);
            } else {
                error = result.message || t.Registration_Error();
            }
        } catch (e) {
            if (e instanceof ApiResponseError) {
                error = e.message || t.Registration_Error();
            } else if (e instanceof Error && e.message.includes('User token is required')) {
                error = t.Registration_Error();
                console.error('Registration failed: user token missing before POST', e);
            } else {
                error = t.Registration_Error();
                console.error('Registration failed:', e);
            }
        } finally {
            submitting = false;
            useRememberedInfo = false;
        }
    }
    
    async function handleUnregister() {
        if (!event) return;

        const eventId = resolveEventId(event, getRouteEventId());
        if (!eventId) {
            error = 'Failed to unregister';
            return;
        }
        
        try {
            submitting = true;
            // Prefer the token from the most recent saveUserProfile()/remember-me
            // load (rememberedToken). It's the real server-issued token; the
            // vvSettings.userToken fallback inside cancelEventRegistration is
            // the mock string on localhost and won't resolve.
            const result = await cancelEventRegistration(eventId, rememberedToken);

            if (result.success) {
                window.location.href = getEventsListUrl();
            } else {
                error = result.message || 'Failed to unregister';
            }
        } catch (e) {
            error = 'Failed to unregister';
            console.error('Unregister failed:', e);
        } finally {
            submitting = false;
            showUnregisterConfirm = false;
        }
    }
    
    type EventDetailSection = 'accommodations' | 'travel' | 'program' | 'resources';

    /** Legacy Register.cshtml jQuery UI accordion: first panel open, rest collapsed. */
    function firstEventDetailSection(ev: Event): EventDetailSection | null {
        if (ev.accommodations?.trim()) return 'accommodations';
        if (ev.travelDetails?.trim()) return 'travel';
        if (ev.program?.trim()) return 'program';
        if (ev.resources?.trim()) return 'resources';
        return null;
    }

    $: openDetailSection = event ? firstEventDetailSection(event) : null;

    $: scheduleDisplay = event ? formatEventScheduleDisplay(event) : undefined;

    $: isRegistered = registration?.isRegistered ?? event?.isRegistered ?? false;
    $: isRegistrationFull = isEventRegistrationFull(event);
</script>

{#if loading}
    <RegisterPageSkeleton />
{:else if error && !event}
    <div class="max-w-4xl mx-auto px-4 py-8">
        <div class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
        </div>
    </div>
{:else if event}
    <div class="w-full m-auto max-w-6xl py-4 px-4 md:px-8 interfont" data-testid="event-register-page">
        <!-- Back Link (Petitions SignPage pattern: chevron separate from underlined label) -->
        <a
            href="/"
            use:link
            class="event-back-nav inline-flex items-center gap-2 rounded-md px-1.5 py-1 text-gray-600 transition-colors hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vv-action-center-button-color,#0d9488)] focus-visible:ring-offset-2 mb-4 md:mb-6"
            data-testid="back-to-events-link"
        >
            <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            <span class="text-base font-medium underline-offset-2 hover:underline">{t.Back_To_Events()}</span>
        </a>

        <!-- Split layout mirrors Petitions SignPage: 50/50 columns, form capped at 426px -->
        <div class="relative flex flex-col lg:flex-row lg:min-h-0">
            <div class="hidden lg:block absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-gray-200 pointer-events-none"></div>

            <!-- Left Column: Event Details -->
            <div class="lg:w-1/2 lg:pr-8">
                <!-- Hero Image (!text-white beats .vvBody h1/span color from host CSS — parity with ActionHero) -->
                <div class="event-register-hero relative rounded-lg overflow-hidden mb-6 h-[250px] md:h-[300px]">
                    {#if event.imageUrl}
                        <img 
                            src={event.imageUrl} 
                            alt={event.title}
                            class="absolute inset-0 w-full h-full object-cover"
                        />
                        <div class="absolute inset-0 bg-black/50"></div>
                    {:else}
                        <div class="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900"></div>
                    {/if}
                    
                    <!-- Gradient Overlay -->
                    <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                    
                    <!-- Registered Badge -->
                    {#if isRegistered}
                        <div class="absolute top-4 left-4">
                            <span class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--vv-action-center-button-color,#0d9488)] !text-[var(--vv-action-center-button-text,#ffffff)] rounded-full text-sm font-medium">
                                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                                </svg>
                                Registered
                            </span>
                        </div>
                    {/if}
                    
                    <!-- Content -->
                    <div class="absolute inset-0 flex flex-col justify-end p-6">
                        <h1 class="!text-2xl md:!text-3xl !font-bold !text-white !leading-tight mb-2 break-words">
                            {event.title}
                        </h1>
                        <div class="flex flex-wrap items-center gap-4 !text-white/90 text-sm">
                            <div class="flex items-center gap-1.5">
                                <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span>{scheduleDisplay}</span>
                            </div>
                            {#if event.location}
                                <div class="flex items-center gap-1.5">
                                    <svg class="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    </svg>
                                    <span>{event.location}</span>
                                </div>
                            {/if}
                        </div>
                    </div>
                </div>
                
                <!-- Description -->
                {#if event.description}
                    <CkEditorContent
                        html={event.description}
                        className="text-base text-gray-600 mb-6"
                        testId="event-description"
                    />
                {/if}
                
                <!-- Accordion Sections -->
                <div class="space-y-3">
                    {#if event.accommodations}
                        <EventAccordion headerText={t.Accommodations()} icon="accommodations" isOpen={openDetailSection === 'accommodations'}>
                            <CkEditorContent html={event.accommodations} className="text-sm text-gray-700" />
                        </EventAccordion>
                    {/if}
                    
                    {#if event.travelDetails}
                        <EventAccordion headerText={t.Travel_Details()} icon="travel" isOpen={openDetailSection === 'travel'}>
                            <CkEditorContent html={event.travelDetails} className="text-sm text-gray-700" />
                        </EventAccordion>
                    {/if}
                    
                    {#if event.program}
                        <EventAccordion headerText={t.Program()} icon="program" isOpen={openDetailSection === 'program'}>
                            <CkEditorContent html={event.program} className="text-sm text-gray-700" />
                        </EventAccordion>
                    {/if}
                    
                    {#if event.resources}
                        <EventAccordion headerText={t.Resources()} icon="resources" isOpen={openDetailSection === 'resources'}>
                            <CkEditorContent html={event.resources} className="text-sm text-gray-700" />
                        </EventAccordion>
                    {/if}
                </div>
            </div>

            <!-- Right Column: Registration Form (same width treatment as Petitions SignPage) -->
            <div class="lg:w-1/2 bg-white mt-8 lg:mt-0" data-testid="event-register-form-column">
                <div class="p-4 lg:pl-8 lg:py-0">
                <div class="w-full max-w-[426px] lg:sticky lg:top-4">
                <div class="bg-white border border-gray-200 rounded-lg p-6 lg:border-0 lg:rounded-none lg:p-0">
                    {#if isRegistrationFull && !isRegistered}
                        <!-- Registration cap reached -->
                        <div class="text-center">
                            <div class="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg class="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <h2 class="text-xl font-semibold text-gray-900 mb-2">{t.Registration_Full_Title()}</h2>
                            <p class="text-sm text-gray-600 mb-6">
                                {t.Registration_Full_Message()}
                            </p>
                            <Button
                                type="action-center"
                                class="w-full"
                                on:click={() => window.location.href = getEventsListUrl()}
                            >
                                {t.Back_To_Events()}
                            </Button>
                        </div>
                    {:else if isRegistered && !showUpdateForm}
                        <!-- Already Registered State -->
                        <div class="text-center">
                            <div
                                class="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                                style="background-color: color-mix(in srgb, var(--vv-action-center-button-color, #0d9488) 15%, #ffffff);"
                            >
                                <svg class="w-8 h-8 text-[var(--vv-action-center-button-color,#0d9488)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h2 class="text-xl font-semibold text-gray-900 mb-2">Registered</h2>
                            <p class="text-sm text-gray-600 mb-6">
                                You're all set! Your registration for this event has been confirmed.
                            </p>
                            
                            <div class="space-y-3">
                                <Button 
                                    type="action-center"
                                    class="w-full"
                                    on:click={openUpdateRegistrationForm}
                                >
                                    {t.Update_Registration()}
                                </Button>
                                
                                {#if !showUnregisterConfirm}
                                    <button
                                        type="button"
                                        class="w-full text-sm text-gray-500 hover:text-red-600 transition-colors pt-2"
                                        on:click={() => showUnregisterConfirm = true}
                                    >
                                        {t.Unregister()}
                                    </button>
                                {:else}
                                    <div class="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
                                        <p class="text-sm text-red-700 mb-3">{t.Unregister_Confirm_Message()}</p>
                                        <div class="flex gap-2">
                                            <button
                                                type="button"
                                                class="flex-1 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                                                on:click={() => showUnregisterConfirm = false}
                                                disabled={submitting}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                class="flex-1 px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                                                on:click={handleUnregister}
                                                disabled={submitting}
                                            >
                                                {submitting ? '...' : 'Unregister'}
                                            </button>
                                        </div>
                                    </div>
                                {/if}
                            </div>
                        </div>
                    {:else}
                        <!-- Registration Form -->
                        <h2 class="text-lg font-semibold text-gray-900 mb-4">{t.Your_Information()}</h2>
                        
                        {#if error}
                            <div class="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg mb-4 text-sm">
                                {error}
                            </div>
                        {/if}

                        {#if showRememberBanner && rememberedUser && !isReturningUserEditing}
                            <RememberMeBanner
                                user={rememberedUser}
                                onUseInfo={continueAsRememberedUser}
                                onEdit={editRememberedUser}
                                onNotMe={clearRememberedUser}
                            />
                        {:else if submitting && useRememberedInfo && !isReturningUserEditing}
                            <div
                                class="flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-white px-6 py-12 text-center"
                                data-testid="remembered-submit-loading"
                            >
                                <div class="mb-4 h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-[var(--vv-action-center-button-color,#0d9488)]"></div>
                                <p class="text-base leading-6 text-gray-600">{t.Submitting_Registration()}</p>
                            </div>
                        {:else}
                            <form on:submit|preventDefault={handleSubmit} class="space-y-4">
                                {#if event.showOtherRegistrationInformation}
                                    <div>
                                        <label class="inline-flex items-center">
                                            <input
                                                type="checkbox"
                                                bind:checked={firstTimeAttendee}
                                                class="text-[var(--vv-action-center-button-color,#0d9488)] focus:ring-[var(--vv-action-center-button-color,#0d9488)] rounded"
                                            />
                                            <span class="ml-2 text-sm text-gray-700">{t.First_Time_Attendee()}</span>
                                        </label>
                                    </div>

                                    <div>
                                        <label for="specialNeeds" class="block text-sm font-medium text-gray-700 mb-1">
                                            {t.Special_Needs()}
                                        </label>
                                        <textarea
                                            id="specialNeeds"
                                            bind:value={specialNeeds}
                                            rows="2"
                                            class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--vv-action-center-button-color,#0d9488)] focus:border-[var(--vv-action-center-button-color,#0d9488)]"
                                        ></textarea>
                                    </div>
                                {/if}

                                <ActionForm
                                    settings={formSettings}
                                    {customFields}
                                    isReturningUser={isReturningUserEditing}
                                    userToken={verifiedIdentity?.token || rememberedToken}
                                    associationPhoneNumber={eventUserContainer?.associationPhoneNumber || ''}
                                    bind:textSubscribe
                                    bind:honorific
                                    bind:firstName
                                    bind:lastName
                                    bind:suffix
                                    bind:email
                                    bind:phone
                                    bind:fax
                                    bind:mobileNumber
                                    bind:street
                                    bind:city
                                    bind:state
                                    bind:zipCode={zip}
                                    bind:businessName
                                    bind:businessTitle
                                    bind:businessStreet
                                    bind:businessCity
                                    bind:businessState
                                    bind:businessZipCode
                                    bind:rememberMe
                                    bind:subscribe
                                    bind:customFieldValues
                                    bind:validatedHomeAddress
                                    bind:validatedBusinessAddress
                                    submitLabel={isRegistered && showUpdateForm ? t.Update_Registration() : t.Submit_Registration()}
                                    submittingLabel={t.Submitting_Registration()}
                                    isSubmitting={submitting}
                                    onSubmit={handleSubmit}
                                />
                            </form>
                        {/if}
                    {/if}
                </div>
                </div>
                </div>
            </div>
        </div>
    </div>
{/if}

<EmailVerification
    bind:isAwaitingVerification={isAwaitingVerification}
    emailAddress={verificationEmailAddress}
    onUserVerified={handleVerifiedUser}
    onNotFound={handleVerificationNotFound}
    onFailure={handleVerificationFailure}
/>

<Modal
    bind:isOpen={isChoosingIdentity}
    headerText={tCommon.Select_Existing_Profile_Header()}
    confirmButtonText={tCommon.Continue_Button()}
    onConfirm={handleIdentityChoiceConfirm}
    onCancel={handleIdentityChoiceCancel}
>
    <p class="text-base text-gray-700 mb-4">
        {tCommon.Multiple_Users_With_Email_Message()}
    </p>
    <div class="space-y-3">
        {#each identityChoices as identity}
            <label class="flex items-start gap-3 text-base text-gray-700 cursor-pointer">
                <input
                    type="radio"
                    name="event-identity-choice"
                    class="w-4 h-4 mt-1 radio-brand"
                    bind:group={selectedIdentityToken}
                    value={identity.token}
                />
                <span>{identity.givenNames} {identity.surname}</span>
            </label>
        {/each}
    </div>
</Modal>
